import AddIcon from '@mui/icons-material/Add'
import ChatIcon from '@mui/icons-material/Chat'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import IconButton from '@mui/material/IconButton'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { useState } from 'react'
import {
  useAddFollowup,
  useConsultants,
  useDisciplines,
  useDrawingFollowups,
  usePendingDrawings,
  useDeleteDrawing,
  useRegisterExpectedDrawing,
  useUpdateDrawing,
  type PendingDrawing,
} from '../api/execution'
import { useMyProjects, useMySites } from '../api/hooks'
import { useAuth } from '../auth/useAuth'
import { ErrorBlock, LoadingBlock } from '../components/Feedback'
import { PageHeader } from '../components/PageHeader'

const SEVERITY: Record<string, { color: string; bg: string }> = {
  CRITICAL: { color: '#8c1d18', bg: '#fbe0de' },
  HIGH: { color: '#9a4a00', bg: '#fdeecf' },
  MEDIUM: { color: '#0d4a8a', bg: '#dceaf8' },
  LOW: { color: '#4a5a68', bg: '#e6ebef' },
}

/**
 * Drawings the site is waiting for: how long, how overdue, and when they were last chased.
 * Days pending is computed on read, so this list is never stale.
 */
export function PendingDrawingsPage() {
  const { can } = useAuth()
  const projects = useMyProjects()
  const [projectId, setProjectId] = useState('')
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(25)
  const [followupFor, setFollowupFor] = useState<PendingDrawing | null>(null)
  const [editing, setEditing] = useState<PendingDrawing | null>(null)
  const [confirming, setConfirming] = useState<PendingDrawing | null>(null)
  const [expectedOpen, setExpectedOpen] = useState(false)
  const removeDrawing = useDeleteDrawing()

  const query = usePendingDrawings({ projectId: projectId || undefined, page, size })
  const rows = query.data?.content ?? []
  const overdue = rows.filter((r) => r.overdue).length
  const neverChased = rows.filter((r) => r.followupCount === 0).length

  return (
    <>
      <PageHeader
        title="Pending drawings"
        subtitle="Drawings not yet in the site's hands. A drawing with no next follow-up date is one nobody is chasing."
        actions={
          can('DRAWING:REQUEST') && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setExpectedOpen(true)}>
              Register awaited drawing
            </Button>
          )
        }
      />

      {(overdue > 0 || neverChased > 0) && (
        <Alert severity={overdue > 0 ? 'error' : 'warning'} sx={{ mb: 2 }}>
          {overdue > 0 && <><strong>{overdue}</strong> past their promised date. </>}
          {neverChased > 0 && <><strong>{neverChased}</strong> never chased.</>}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField select label="Project" value={projectId} sx={{ minWidth: 260 }}
                   onChange={(e) => { setProjectId(e.target.value); setPage(0) }}>
          <MenuItem value="">All projects</MenuItem>
          {(projects.data ?? []).map((p) => (
            <MenuItem key={p.id} value={p.id}>{p.code} - {p.name}</MenuItem>
          ))}
        </TextField>
      </Paper>

      {query.isLoading && <LoadingBlock />}
      {query.error && <ErrorBlock error={query.error} />}

      {query.data && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Drawing no.</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Consultant</TableCell>
                <TableCell align="right">Days pending</TableCell>
                <TableCell>Expected</TableCell>
                <TableCell align="right">Overdue by</TableCell>
                <TableCell>Last chased</TableCell>
                <TableCell>Next chase</TableCell>
                <TableCell align="right">Chases</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((d) => (
                <TableRow key={d.drawingId} hover>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{d.drawingNumber}</TableCell>
                  <TableCell>{d.title}</TableCell>
                  <TableCell>{d.consultantName ?? '-'}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {d.daysPending != null ? `${d.daysPending}d` : '-'}
                  </TableCell>
                  <TableCell>{d.expectedDate ?? '-'}</TableCell>
                  <TableCell align="right">
                    {d.overdueByDays != null ? (
                      <strong style={{ color: '#b3261e' }}>{d.overdueByDays}d</strong>
                    ) : '-'}
                  </TableCell>
                  <TableCell>{d.lastFollowupDate ?? <em>never</em>}</TableCell>
                  <TableCell>
                    {d.nextFollowupDate ?? (
                      <Tooltip title="Nobody has set a date to chase this">
                        <Chip label="not set" size="small" sx={{ bgcolor: '#fdeecf', color: '#9a4a00' }} />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="right">{d.followupCount}</TableCell>
                  <TableCell>
                    <Chip label={d.severity.toLowerCase()} size="small"
                          sx={{ fontWeight: 700, ...(SEVERITY[d.severity] ?? SEVERITY.LOW) }} />
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Button size="small" startIcon={<ChatIcon />} onClick={() => setFollowupFor(d)}>
                      Chase
                    </Button>
                    {can('DRAWING:UPDATE') && (
                      <Tooltip title="Edit register entry">
                        <IconButton size="small" onClick={() => setEditing(d)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {can('DRAWING:DELETE') && (
                      <Tooltip title="Remove from the register">
                        <IconButton size="small" color="error" onClick={() => setConfirming(d)}>
                          <DeleteOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                    Nothing outstanding. Register a drawing you are waiting for to track it here.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={query.data.page.totalElements}
            page={query.data.page.number}
            rowsPerPage={query.data.page.size}
            onPageChange={(_, n) => setPage(n)}
            onRowsPerPageChange={(e) => { setSize(Number(e.target.value)); setPage(0) }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </TableContainer>
      )}

      {followupFor && (
        <FollowupDialog drawing={followupFor} onClose={() => setFollowupFor(null)} />
      )}
      {editing && <RegisterEditDialog drawing={editing} onClose={() => setEditing(null)} />}

      <Dialog open={Boolean(confirming)} onClose={() => setConfirming(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove {confirming?.drawingNumber}?</DialogTitle>
        <DialogContent>
          {Boolean(removeDrawing.error) && (
            <Box sx={{ mb: 2 }}>
              <ErrorBlock error={removeDrawing.error} title="Could not remove it" />
            </Box>
          )}
          <Typography variant="body2" color="text.secondary">
            It leaves the register and the pending list along with its chase history. Remove it
            only if the drawing was never actually needed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={removeDrawing.isPending}
            onClick={async () => {
              if (confirming) await removeDrawing.mutateAsync(confirming.drawingId)
              setConfirming(null)
            }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <ExpectedDrawingDialog open={expectedOpen} onClose={() => setExpectedOpen(false)} />
    </>
  )
}

/**
 * Editing the register entry: what the drawing is, who owes it, when it is due.
 *
 * <p>Separate from correcting a receipt, which belongs to the day it was received. This is
 * the entry itself, and it is what the chase list is built from.</p>
 */
function RegisterEditDialog({ drawing, onClose }: { drawing: PendingDrawing; onClose: () => void }) {
  const consultants = useConsultants()
  const disciplines = useDisciplines()
  const update = useUpdateDrawing()
  const [form, setForm] = useState({
    drawingNumber: drawing.drawingNumber,
    title: drawing.title,
    consultantId: drawing.consultantId ?? '',
    disciplineId: drawing.disciplineId ?? '',
    requestedDate: drawing.requestedDate ?? '',
    expectedDate: drawing.expectedDate ?? '',
    responsibleParty: drawing.responsibleParty ?? '',
    remarks: drawing.remarks ?? '',
  })
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm({ ...form, [k]: e.target.value })

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit {drawing.drawingNumber}</DialogTitle>
      <DialogContent dividers>
        {Boolean(update.error) && (
          <Box sx={{ mb: 2 }}>
            <ErrorBlock error={update.error} title="Could not save the change" />
          </Box>
        )}
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
          <TextField label="Drawing number" required value={form.drawingNumber}
                     onChange={set('drawingNumber')} />
          <TextField label="Title" required value={form.title} onChange={set('title')} />
          <TextField select label="Consultant" value={form.consultantId} onChange={set('consultantId')}>
            <MenuItem value="">Not specified</MenuItem>
            {(consultants.data ?? []).map((c) => (
              <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
            ))}
          </TextField>
          <TextField select label="Discipline" value={form.disciplineId} onChange={set('disciplineId')}>
            <MenuItem value="">Not specified</MenuItem>
            {(disciplines.data ?? []).map((d) => (
              <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
            ))}
          </TextField>
          <TextField label="First requested" type="date" value={form.requestedDate}
                     onChange={set('requestedDate')} slotProps={{ inputLabel: { shrink: true } }} />
          <TextField label="Expected by" type="date" value={form.expectedDate}
                     onChange={set('expectedDate')} slotProps={{ inputLabel: { shrink: true } }}
                     helperText="Drives the overdue count" />
          <TextField label="Responsible party" value={form.responsibleParty}
                     onChange={set('responsibleParty')} sx={{ gridColumn: { sm: '1 / -1' } }} />
          <TextField label="Remarks" value={form.remarks} onChange={set('remarks')} multiline
                     minRows={2} sx={{ gridColumn: { sm: '1 / -1' } }} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={update.isPending || !form.drawingNumber.trim() || !form.title.trim()}
          onClick={async () => {
            await update.mutateAsync({
              id: drawing.drawingId,
              body: {
                drawingNumber: form.drawingNumber.trim(),
                title: form.title.trim(),
                siteId: drawing.siteId,
                consultantId: form.consultantId || undefined,
                disciplineId: form.disciplineId || undefined,
                drawingTypeId: drawing.drawingTypeId,
                firstRequestedDate: form.requestedDate || undefined,
                expectedDate: form.expectedDate || undefined,
                responsibleParty: form.responsibleParty || undefined,
                remarks: form.remarks || undefined,
              },
            })
            onClose()
          }}
        >
          {update.isPending ? 'Saving...' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function FollowupDialog({ drawing, onClose }: { drawing: PendingDrawing; onClose: () => void }) {
  const history = useDrawingFollowups(drawing.drawingId)
  const add = useAddFollowup()
  const [f, setF] = useState({
    followupDate: new Date().toISOString().slice(0, 10),
    followupWith: drawing.consultantName ?? '',
    communicationMode: 'EMAIL',
    remarks: '',
    expectedDate: '',
    nextFollowupDate: '',
  })

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        Chase {drawing.drawingNumber}
        <Typography variant="body2" color="text.secondary">{drawing.title}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        {add.error != null && <Box sx={{ mb: 2 }}><ErrorBlock error={add.error} /></Box>}
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' } }}>
          <TextField label="Date chased" type="date" value={f.followupDate}
                     onChange={(e) => setF({ ...f, followupDate: e.target.value })}
                     slotProps={{ inputLabel: { shrink: true } }} />
          <TextField label="Spoke to" value={f.followupWith}
                     onChange={(e) => setF({ ...f, followupWith: e.target.value })} />
          <TextField select label="How" value={f.communicationMode}
                     onChange={(e) => setF({ ...f, communicationMode: e.target.value })}>
            {['EMAIL', 'PHONE', 'WHATSAPP', 'MEETING', 'LETTER', 'OTHER'].map((m) => (
              <MenuItem key={m} value={m}>{m.toLowerCase()}</MenuItem>
            ))}
          </TextField>
          <TextField label="Date they promised" type="date" value={f.expectedDate}
                     onChange={(e) => setF({ ...f, expectedDate: e.target.value })}
                     slotProps={{ inputLabel: { shrink: true } }}
                     helperText="Updates the register" />
          <TextField label="Chase again on" type="date" value={f.nextFollowupDate}
                     onChange={(e) => setF({ ...f, nextFollowupDate: e.target.value })}
                     slotProps={{ inputLabel: { shrink: true } }} />
        </Box>
        <TextField label="What was said" fullWidth multiline minRows={2} sx={{ mt: 2 }}
                   value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} />

        <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
          Previous chases ({history.data?.length ?? 0})
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>By</TableCell>
                <TableCell>With</TableCell>
                <TableCell>How</TableCell>
                <TableCell>Promised</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(history.data ?? []).map((h) => (
                <TableRow key={h.id}>
                  <TableCell>{h.followupDate}</TableCell>
                  <TableCell>{h.followupByName ?? '-'}</TableCell>
                  <TableCell>{h.followupWith ?? '-'}</TableCell>
                  <TableCell>{h.communicationMode.toLowerCase()}</TableCell>
                  <TableCell>{h.expectedDate ?? '-'}</TableCell>
                  <TableCell>{h.remarks ?? '-'}</TableCell>
                </TableRow>
              ))}
              {(history.data?.length ?? 0) === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 2, color: 'text.secondary' }}>
                    Never chased.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" disabled={add.isPending}
                onClick={async () => {
                  await add.mutateAsync({
                    drawingId: drawing.drawingId,
                    body: {
                      followupDate: f.followupDate,
                      followupWith: f.followupWith || undefined,
                      communicationMode: f.communicationMode,
                      remarks: f.remarks || undefined,
                      expectedDate: f.expectedDate || undefined,
                      nextFollowupDate: f.nextFollowupDate || undefined,
                    },
                  })
                  onClose()
                }}>
          {add.isPending ? 'Saving...' : 'Record chase'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function ExpectedDrawingDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const projects = useMyProjects()
  const sites = useMySites()
  const consultants = useConsultants()
  const disciplines = useDisciplines()
  const register = useRegisterExpectedDrawing()
  const [f, setF] = useState({
    projectId: '', siteId: '', drawingNumber: '', title: '',
    consultantId: '', disciplineId: '',
    requestedDate: new Date().toISOString().slice(0, 10), expectedDate: '',
  })

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Register a drawing you are waiting for</DialogTitle>
      <DialogContent dividers>
        {register.error != null && <Box sx={{ mb: 2 }}><ErrorBlock error={register.error} /></Box>}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This puts the drawing on the pending list so it can be chased. Recording it as
          received later updates the same entry and keeps the days-pending count.
        </Typography>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
          <TextField select label="Project" required value={f.projectId}
                     onChange={(e) => setF({ ...f, projectId: e.target.value })}>
            {(projects.data ?? []).map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.code} - {p.name}</MenuItem>
            ))}
          </TextField>
          <TextField select label="Site (optional)" value={f.siteId}
                     onChange={(e) => setF({ ...f, siteId: e.target.value })}>
            <MenuItem value="">Project-wide</MenuItem>
            {(sites.data ?? []).filter((s) => !f.projectId || s.projectId === f.projectId)
              .map((s) => <MenuItem key={s.id} value={s.id}>{s.code} - {s.name}</MenuItem>)}
          </TextField>
          <TextField label="Drawing number" required value={f.drawingNumber}
                     onChange={(e) => setF({ ...f, drawingNumber: e.target.value })} />
          <TextField label="Title" required value={f.title}
                     onChange={(e) => setF({ ...f, title: e.target.value })} />
          <TextField select label="Expected from (consultant)" value={f.consultantId}
                     onChange={(e) => setF({ ...f, consultantId: e.target.value })}>
            <MenuItem value="">Not specified</MenuItem>
            {(consultants.data ?? []).map((c) => (
              <MenuItem key={c.id} value={c.id}>{c.name}{c.extra ? ` — ${c.extra}` : ''}</MenuItem>
            ))}
          </TextField>
          <TextField select label="Discipline" value={f.disciplineId}
                     onChange={(e) => setF({ ...f, disciplineId: e.target.value })}>
            <MenuItem value="">Not specified</MenuItem>
            {(disciplines.data ?? []).map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
          </TextField>
          <TextField label="Requested on" type="date" value={f.requestedDate}
                     onChange={(e) => setF({ ...f, requestedDate: e.target.value })}
                     slotProps={{ inputLabel: { shrink: true } }} />
          <TextField label="Expected by" type="date" value={f.expectedDate}
                     onChange={(e) => setF({ ...f, expectedDate: e.target.value })}
                     slotProps={{ inputLabel: { shrink: true } }}
                     helperText="Drives the overdue flag" />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained"
                disabled={register.isPending || !f.projectId || !f.drawingNumber.trim() || !f.title.trim()}
                onClick={async () => {
                  await register.mutateAsync({
                    projectId: f.projectId,
                    siteId: f.siteId || undefined,
                    drawingNumber: f.drawingNumber.trim(),
                    title: f.title.trim(),
                    consultantId: f.consultantId || undefined,
                    disciplineId: f.disciplineId || undefined,
                    requestedDate: f.requestedDate || undefined,
                    expectedDate: f.expectedDate || undefined,
                  })
                  onClose()
                }}>
          {register.isPending ? 'Saving...' : 'Register'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
